import { Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { StateObservable, ofType, combineEpics } from 'redux-observable'
import { GlobalState } from 'store'
import { SelectionActionTypes, SelectionActions } from 'store/selection'
import { GridId } from 'store/grid'
import {
  currentGridIdSelector,
  createSelectedCollectionIdSelector,
  createGridActivityHighlightSelector
} from 'selectors/selectionSelectors'
import { BTQAction } from 'epics'
import { SHOULD_AUTOBATCH } from '@reduxjs/toolkit'

function gridActivityHighlightedFilter(gridId: GridId, state: GlobalState) {
  const gridActivityHighlighted = createGridActivityHighlightSelector(gridId)(state)
  return gridActivityHighlighted
}

export type SetCurrentGridIdAction = ReturnType<typeof SelectionActions.setCurrentGridId>

export function setCurrentGridIdEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, SelectionActionTypes.SET_CURRENT_GRID_ID, SetCurrentGridIdAction>(SelectionActionTypes.SET_CURRENT_GRID_ID),
    filter(action => !!action.payload.gridId),
    filter(action => {
      const selectedCollectionId = createSelectedCollectionIdSelector(action.payload.gridId)(state$.value)
      return !selectedCollectionId
    }),
    filter(action => gridActivityHighlightedFilter(action.payload.gridId!, state$.value)),
    map(action => SelectionActions.setGridActivityHighlight(action.payload.gridId!, false))
  )
}

export type SetGridActivityHighlightAction = ReturnType<typeof SelectionActions.setGridActivityHighlight>

export function setGridActivityHighlightEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, SelectionActionTypes.SET_GRID_ACTIVITY_HIGHLIGHT, SetGridActivityHighlightAction>(
      SelectionActionTypes.SET_GRID_ACTIVITY_HIGHLIGHT
    ),
    filter(action => action.payload.showHighlight),
    filter(action => {
      const currentGridId = currentGridIdSelector(state$.value)
      const selectedCollectionId = createSelectedCollectionIdSelector(currentGridId)(state$.value)
      return action.payload.gridId === currentGridId && !selectedCollectionId
    }),
    filter(action => gridActivityHighlightedFilter(action.payload.gridId, state$.value)),
    map(action => SelectionActions.setGridActivityHighlight(action.payload.gridId, false, action.meta[SHOULD_AUTOBATCH]))
  )
}

export type SetCollectionIdAction = ReturnType<typeof SelectionActions.setCollectionId>

export function setCollectionIdEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, SelectionActionTypes.SET_COLLECTION_ID, SetCollectionIdAction>(SelectionActionTypes.SET_COLLECTION_ID),
    filter(action => action.payload.collectionId === null),
    filter(action => gridActivityHighlightedFilter(action.payload.gridId, state$.value)),
    map(action => SelectionActions.setGridActivityHighlight(action.payload.gridId, false))
  )
}

export const gridEpic = combineEpics(setCurrentGridIdEpic, setGridActivityHighlightEpic, setCollectionIdEpic)
